import { memo, useMemo } from 'react'

import { FileItem } from '@inno-sdks/files'
import { CellTypes, Row, Table } from '@inno-ui/table'
import { useGetFileItem } from 'modules/desktop/modules/files-integration/api'
import {
  formatDateTimeWithLocale,
  formatFileSize,
  getUserAvatarUrl
} from 'modules/desktop/modules/files-integration/utils'

import { useItemModal } from '../../hooks/useItemModal'
import styles from '../Properties.module.scss'
import { TableColumnIds, columns } from '../constants'
import { useGetPropertyNameColumn } from '../hooks'

export const FileProperties = memo(() => {
  const { itemId } = useItemModal()
  const { data: item } = useGetFileItem<FileItem>(itemId)

  const getPropertyNameColumn = useGetPropertyNameColumn()

  const rows = useMemo<Array<Row>>(
    () => [
      {
        id: 'type',
        cells: [
          getPropertyNameColumn('type'),
          {
            type: CellTypes.Text,
            columnId: TableColumnIds.Value,
            props: { value: item?.metaData.mimeType }
          }
        ]
      },
      {
        id: 'extension',
        cells: [
          getPropertyNameColumn('extension'),
          {
            type: CellTypes.Text,
            columnId: TableColumnIds.Value,
            props: { value: item?.metaData.extension }
          }
        ]
      },
      {
        id: 'size',
        cells: [
          getPropertyNameColumn('size'),
          {
            type: CellTypes.Text,
            columnId: TableColumnIds.Value,
            props: { value: formatFileSize(item?.metaData.size) }
          }
        ]
      },
      ...((item?.sharedBy
        ? [
            {
              id: 'shared-by',
              cells: [
                getPropertyNameColumn('shared-by'),
                {
                  type: CellTypes.Person,
                  columnId: TableColumnIds.Value,
                  props: {
                    name: item.sharedBy.name,
                    avatar: getUserAvatarUrl(item.sharedBy.avatar)
                  }
                }
              ]
            }
          ]
        : []) as Array<Row>),
      {
        id: 'created-by',
        cells: [
          getPropertyNameColumn('created-by'),
          {
            type: CellTypes.Person,
            columnId: TableColumnIds.Value,
            props: {
              name: item?.createdBy?.name ?? 'N\\A',
              avatar: getUserAvatarUrl(item?.createdBy?.avatar)
            }
          }
        ]
      },
      {
        id: 'created-at',
        cells: [
          getPropertyNameColumn('created-at'),
          {
            type: CellTypes.Text,
            columnId: TableColumnIds.Value,
            props: {
              value: formatDateTimeWithLocale(item?.createdAt ?? new Date())
            }
          }
        ]
      },
      {
        id: 'updated-at',
        cells: [
          getPropertyNameColumn('updated-at'),
          {
            type: CellTypes.Text,
            columnId: TableColumnIds.Value,
            props: {
              value: formatDateTimeWithLocale(item?.updatedAt ?? new Date())
            }
          }
        ]
      }
    ],
    [item, getPropertyNameColumn]
  )

  if (!item) {
    return null
  }

  return (
    <div className={styles.properties}>
      <div className={styles.table}>
        <Table
          id='properties'
          columns={columns}
          rows={rows}
          withHeader={false}
        />
      </div>
    </div>
  )
})
